import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchUserYardId, resetStore } from "@/lib/yard-data";

export interface Session {
  email: string;
  role: "admin" | "dispatcher" | "gate_agent" | "viewer";
  yardConfigured: boolean;
}

interface AuthValue {
  session: Session | null;
  ready: boolean;
  signIn: (email: string, password: string, captchaToken: string) => Promise<void>;
  signUp: (email: string, password: string, captchaToken: string) => Promise<void>;

  completeYardSetup: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * "Onboarded" means a real yards row exists for this user's org — never a
 * localStorage flag. Accounts previously marked done with an empty yards
 * table therefore self-heal straight back into onboarding.
 */
async function yardExists(): Promise<boolean> {
  try {
    return (await fetchUserYardId()) !== null;
  } catch {
    return false;
  }
}


/**
 * Non-sensitive presence flag so the server can decide, before rendering,
 * whether "/" is the marketing page or the Gate Manifest. It carries no
 * credentials — every data path is still gated by Supabase RLS.
 */
function markSessionCookie(active: boolean) {
  if (typeof document === "undefined") return;
  document.cookie = active
    ? "pd_session=1; path=/; max-age=2592000; SameSite=Lax"
    : "pd_session=; path=/; max-age=0; SameSite=Lax";
}



export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const hydrate = useCallback(async (user: { id: string; email?: string | null } | null) => {
    if (!user) {
      markSessionCookie(false);
      setUserId(null);
      setSession(null);
      setReady(true);
      return;
    }
    markSessionCookie(true);
    setUserId(user.id);

    let role: Session["role"] = "viewer";
    try {
      const { data } = await supabase
        .from("org_members")
        .select("role")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();
      if (data?.role) role = data.role as Session["role"];
    } catch {
      /* keep least-privilege default */
    }
    setSession({ email: user.email ?? "", role, yardConfigured: await yardExists() });
    setReady(true);
  }, []);

  useEffect(() => {
    let active = true;

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!active) return;
      void hydrate(s?.user ?? null);
    });

    void supabase.auth.getUser().then(({ data }) => {
      if (active) void hydrate(data.user ?? null);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [hydrate]);

  const value = useMemo<AuthValue>(
    () => ({
      session,
      ready,
      signIn: async (email, password, captchaToken) => {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
          options: { captchaToken },
        });
        if (error) throw error;
      },
      signUp: async (email, password, captchaToken) => {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { captchaToken, emailRedirectTo: `${window.location.origin}/auth/callback` },
        });
        if (error) throw error;
      },
      completeYardSetup: async () => {
        if (!userId) return;
        // Re-read from the database; only a real row flips the flag.
        const exists = await yardExists();
        setSession((s) => (s ? { ...s, yardConfigured: exists } : s));
      },
      signOut: async () => {
        await supabase.auth.signOut();
        resetStore();
        setSession(null);
        setUserId(null);
      },
    }),
    [session, ready, userId],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
